// Two-Step Acceptance Gate — client bootstrap and enforcement state.
//
// This context is the AUTHORITATIVE frontend view of whether the current
// authenticated Circle member has satisfied the two mandatory acceptance
// steps for the CURRENT server-side legal versions. It is a defence-in-
// depth companion to the backend gate; the server is always authoritative
// (§25).
//
// Contract:
//   * On mount, once the AuthContext resolves an authenticated user, we
//     fetch /me/acceptance-status. Until this resolves, ``ready`` is false
//     and no protected content must be rendered (§27).
//   * ``required_next`` — null when both current terms are accepted, else
//     the type of the NEXT step the member must complete.
//   * ``refresh()`` — re-queries the server; called after every acceptance
//     POST and whenever the axios interceptor sees 403/409 (§26/§29).
//   * ``failure`` — populated when acceptance-status or legal-text fetches
//     fail. Views must show only Retry/Logout in that state (§28).

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/modules/growing-circle/lib/api";
import { useAuth } from "@/modules/growing-circle/context/AuthContext";

const AcceptanceContext = createContext(null);

export function AcceptanceProvider({ children }) {
  const { user, ready: authReady } = useAuth();
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState(null);
  const [failure, setFailure] = useState(null);
  // We remember the deep-link the user attempted to reach so we can restore
  // it after they complete both steps (§40). Kept in memory only.
  const originalUrlRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus(null);
      setReady(true);
      return;
    }
    // Admins accessing pure Back Office (/growing-circle/admin/*) are not
    // gated by the member acceptance. The layout that mounts this provider
    // decides whether to render the gate; here we always fetch so the
    // MemberLayout has fresh status if the admin also enters member routes.
    try {
      setFailure(null);
      const { data } = await api.get("/me/acceptance-status");
      setStatus(data);
      setReady(true);
    } catch (err) {
      setFailure({ code: "acceptance_status_error", message: err?.message || "network" });
      setReady(true);
    }
  }, [user]);

  useEffect(() => {
    if (!authReady) return;
    // Reset any previous state whenever the authenticated identity changes.
    setReady(false);
    setStatus(null);
    refresh();
  }, [authReady, user, refresh]);

  // Hook the axios interceptor into this context so mid-session 403
  // acceptance_required (§29) and 409 terms_version_changed (§26) immediately
  // remount the gate and re-fetch the current legal state.
  useEffect(() => {
    const onEvent = () => refresh();
    window.addEventListener("gc:acceptance-invalidated", onEvent);
    return () => window.removeEventListener("gc:acceptance-invalidated", onEvent);
  }, [refresh]);

  const captureOriginalUrl = useCallback((url) => {
    // Never overwrite an existing capture — the first requested URL wins.
    if (!originalUrlRef.current && url) originalUrlRef.current = url;
  }, []);

  const consumeOriginalUrl = useCallback(() => {
    const u = originalUrlRef.current;
    originalUrlRef.current = null;
    return u;
  }, []);

  const value = {
    ready,
    status,
    failure,
    refresh,
    captureOriginalUrl,
    consumeOriginalUrl,
  };
  return <AcceptanceContext.Provider value={value}>{children}</AcceptanceContext.Provider>;
}

export const useAcceptance = () => useContext(AcceptanceContext);
